import styled from "@emotion/styled";
import { RequiredThemeKeys } from "@smartsheet/sds-theme-internal";
import { FocusMode } from "../../focusMode";
import { formInputTheme, getStylingState } from "./formInputTheme";

interface StyledInputProps {
    focusMode: FocusMode;
    hasContentAfter: boolean;
    hasContentBefore: boolean;
    isFocused: boolean;
    isHovered: boolean;
    themeMode: RequiredThemeKeys;
    validationState: "error" | undefined;
    variant: "box" | "underline";
}

export const StyledInput = styled("input")<StyledInputProps>`
    font-family: inherit;
    font-size: ${({ themeMode, variant }) => formInputTheme.fontSize[variant](themeMode)};
    line-height: ${({ themeMode }) => formInputTheme.lineHeight(themeMode)}px;
    font-weight: ${({ themeMode }) => formInputTheme.fontWeight(themeMode)};

    box-sizing: border-box;
    margin: 0;
    padding: 0px ${getPaddingRight} 0px ${getPaddingLeft};
    width: 100%;
    outline: none;

    color: ${({ themeMode }) => `var(--sds--form--color--value, ${formInputTheme.textColor.rest(themeMode)})`};
    background-color: transparent;

    &::placeholder {
        color: ${({ themeMode, isHovered }) =>
            isHovered
                ? formInputTheme.placeholderColor.hover(themeMode)
                : `var(--sds--form--color--placeholder, ${formInputTheme.placeholderColor.rest(themeMode)})`};
    }
    &[aria-disabled="true"] {
        color: ${({ themeMode }) => formInputTheme.textColor.readonly(themeMode)};
        cursor: not-allowed;
        &::placeholder {
            color: ${({ themeMode }) =>
                `var(--sds--form--color--placeholder, ${formInputTheme.placeholderColor.rest(themeMode)})`};
        }
    }

    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;

    border-style: none;
    // Use the outer component's radius.
    border-radius: inherit;
`;

// The following functions are necessary because the padding of the input is dependent on the
// presence of content before/after. In Ursa, with content before/after the padding is 8px. Without, it's 12px (really 11px due to the border).
function getPaddingLeft({
    themeMode,
    hasContentBefore,
    validationState,
    isFocused,
    focusMode,
    variant,
}: StyledInputProps) {
    if (!hasContentBefore && variant !== "underline") {
        return `${formInputTheme.padding.default[getStylingState({ validationState, isFocused, focusMode })](
            themeMode
        )}px`;
    } else if (variant === "underline") {
        return `${formInputTheme.padding.default.rest(themeMode)}px`;
    } else {
        return `${formInputTheme.padding.betweenContent(themeMode)}`;
    }
}

function getPaddingRight({
    themeMode,
    hasContentAfter,
    validationState,
    isFocused,
    focusMode,
    variant,
}: StyledInputProps) {
    if (!hasContentAfter && variant !== "underline") {
        return `${formInputTheme.padding.default[getStylingState({ validationState, isFocused, focusMode })](
            themeMode
        )}px`;
    } else if (variant === "underline") {
        return `${formInputTheme.padding.default.rest(themeMode)}px`;
    } else {
        return `${formInputTheme.padding.betweenContent(themeMode)}`;
    }
}
